import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import TemplateTable from '../shared/tables/TemplateTable';
import { ITableHeader } from '../shared/tables/models/ITableHeader';
import { useNavigate, useParams } from 'react-router-dom';
import BiButton from '../primitives/buttons/BiButton.primitive';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';
import LoaderContainer from '../shared/loaderContainer/LoaderContainer';
import { setToastData } from '../../app/toastReducer';

import { getLoggedInOrg } from '../../util/admin-utils';
import { noDataAvailableMessage } from '../Organizations/helpers';
import { HTTP_STATUS } from '../../http/constants/http.status';
import { getFailureMessage } from '../../util/ErrorUtil';
import { AppConstants } from '../../constants/AppConstants';
import { useQuery } from '../../hooks/queryParams';
import { RouteConstants } from '../../constants/RouteConstants';
import { useTheme } from '../../context/themeContext';
import { useTranslation } from 'react-i18next';
import { map, find, isEmpty } from 'lodash';
import moment from 'moment';
import { useDidMountEffect } from '../../util/customHooks';
import AddCircle from '../../assets/addCircle.svg';
import { setTableOptions } from '../../app/templateTableReducer';
import { EllipsisDiv } from '../../styles/globalStyles';
import { listFeatures } from '../../http/access-management-service';
import { setFeatureData, setOrgList } from '../../app/rolesAndFeatureReducer';
import { checkFeaturePermission } from '../../util/permissionUtils';
import {
  getChildOrganizations,
  getOrganization,
  getOrganizations
} from '../../http/organization-services';
import { a } from '../Migration/Details/ModulesTable';
import { getUserLoggedInOrg } from '../../util/OrganizationUtil';
import { setBreadcrump } from '../../app/organizationReducer';

export interface filterConfigProps {
  type: string;
  name: string;
}

const CustomersTableContainer = styled.div`
  padding: 1rem;
  width: 100%;
`;

/**
 * @parentId feature
 * @manager Features Listing
 * @overview Lists Features
 *   <section>
 *       <p>
 *          The Features listing page allows administrators to view and manage all system features. This page is accessible through the <b>Features</b> menu under the <strong>Security</strong> sidebar.
 *          Users can navigate through features using pagination controls, with options to display 20, 50, or 100 items per page.
 *       </p>
 *       <br>
 *       <p>
 *          Administrators can filter features by <b>Status</b>, <b>Actions</b>, and <b>Tags</b>. The <b>Create New Feature</b> button enables users with the appropriate permissions to create new features.
 *       </p>
 *       <br>
 *       <p>
 *           Each feature is displayed in a table with columns for:
 *           <ul>
 *               <li><b>Status</b>: Indicates if the feature is active or inactive.</li>
 *               <li><b>Name</b>: The unique name of the feature.</li>
 *               <li><b>Description</b>: A brief description of the feature's purpose and functionality.</li>
 *               <li><b>Actions</b>: Available actions for the feature.</li>
 *               <li><b>Tags</b>: Labels associated with the feature for easy searching and categorization.</li>
 *           </ul>
 *       </p>
 *    <p>
 *       <h5>Table Display:</h5>
 *        <ul>
 *          <li><b>Status:</b> Displays the feature status.</li>
 *          <li><b>Name:</b> Displays the feature name.</li>
 *          <li><b>Description:</b> Displays the feature description.</li>
 *          <li><b>Actions:</b> Lists operations associated with the feature.</li>
 *          <li><b>Tags:</b> Lists tags associated with the feature.</li>
 *          <li><b>Created On:</b> Shows the creation date of the feature.</li>
 *          <li><b>Last Modified On:</b> Shows the last modification date of the feature.</li>
 *        </ul>
 *   </p>
 *
 *       <p>
 *           Features can be sorted by <i>Status</i>, <i>Name</i>, <i>Description</i>, and timestamps for <i>Creation</i> and <i>Last Modified On</i>.
 *       </p>
 *     </section>
 *       <section>
 *         <p>
 *          <h4>Filters</h4>
 *          Users can apply the following filters to narrow down feature results:
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Type</th>
 *               <th>Description</th>
 *            </tr>
 *              <tr>
 *               <td><b>Status</b></td>
 *               <td>Dropdown</td>
 *               <td>Selects features by their current status, such as Active or Inactive.</td>
 *            </tr>
 *             <tr>
 *               <td><b>Actions</b></td>
 *               <td>Text Field</td>
 *              <td>Filters features based on associated actions. Enter multiple actions, separated by commas, to refine your search.</td>
 *            </tr>
 *             <tr>
 *               <td><b>Tags</b></td>
 *               <td>Text Field</td>
 *               <td>Filters by specific tags assigned to features. Enter multiple tags, separated by commas, to refine your search.</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *     <section>
 *      <h4>Error Handling</h4>
 *       <p>
 *       This section details potential error responses and their meanings:
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>FEATURE_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page.</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>FEATURE_INTERNAL_ERROR</td>
 *               <td>An unexpected error occurred. Please try again later.</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td>SERVICE_UNAVAILABLE</td>
 *               <td>The service is temporarily unavailable. Please retry later.</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section>
 *   <section>
 *      <h4>Dependencies and Permissions</h4>
 *      <p>This section outlines the dependencies and permissions required for the Features listing page:</p>
 *       <h5>System Settings</h5>
 *       <p>Configurations that impact feature visibility and behavior:</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Services</h5>
 *       <br>
 *     <p>Required services and their versions:</p>
 *     <br>
 *     <table>
 *       <tr>
 *           <th>Service Name</th>
 *           <th>Version</th>
 *       </tr>
 *       <tr>
 *           <td>Access Control Service</td>
 *           <td>1.0.0</td>
 *       </tr>
 *       <tr>
 *           <td colspan="2">API-ACS52 List</td>
 *       </tr>
 *     </table>
 *       <br>
 *      <h5>API Role Permissions</h5>
 *      <p>Permissions needed for accessing API endpoints:</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>/features</td>
 *               <td>GET</td>
 *               <td>accesscontrol-service.feature.list</td>
 *               <td>Yes</td>
 *           </tr>
 *       </table>
 *   </section>
 *   <section>
 *    <p>Sequence Diagram for Features listing page interaction flow:</p>
 *   </section>
 */

const FeaturesList = () => {
  const query = useQuery();
  const params = useParams();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [pagingOpts, setPagingOpts] = useState(null);
  const [size, setSize] = useState(AppConstants.DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(AppConstants.DEFAULT_PAGE);

  const orgList = useAppSelector((state) => state.rolesAndFeature?.orgList);
  const userProfile = useAppSelector((state) => state.userManagement.userProfileInfo);
  const loggedInOrg = getLoggedInOrg(userProfile);

  const [rows, setRows] = useState<any>([]);
  const [paramObj, setParamObj] = useState({
    status: ''
  });

  const [formFilterData, setFormFilterData] = useState<filterConfigProps>({
    type: '',
    name: ''
  });

  const [loadNextData, setLoadNextData] = useState<any>({
    orgPaging: false,
    searchOrgName: '',
    searchOrg: false
  });

  const searchOrgData = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchOrgName: data,
      searchOrg: true,
      orgPaging: false
    }));
  };

  const fetchMoreOrgData = () => {
    setLoadNextData((prevState: any) => ({ ...prevState, orgPaging: true }));
  };

  useDidMountEffect(async () => {
    if (loadNextData.searchOrg) {
      loadOrgSearchData();
    }
  }, [loadNextData]);

  const filterOrgNames = (keyword: string, orgList: any = []) => {
    return orgList.filter((org: any) => org.name.toLowerCase().includes(keyword.toLowerCase()));
  };

  const getOrgList = (orgResponse: any) => {
    const { data: orgList = [] } = orgResponse || {};
    return orgResponse;
  };

  const updateFilterSetData = (orgResponse: any) => {
    let initialFilterList: any = [...filterList];

    const currentFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === 'orgFilter') {
        return {
          ...obj,
          itemsList: getOrgList(orgResponse)
        };
      } else {
        return { ...obj };
      }
    });
    setFilterList(currentFilterList);
  };

  const updateSelectedFilter = async (
    filterList: any,
    filterPillsData: any = [],
    orgId: any,
    fetchMoreOrgData: any,
    setFilterPillsData: any,
    setFilterList: any,
    setFilterCallback: any = () => {},
    loadNextData: any = () => {}
  ) => {
    let initialFilterList: any = [...filterList];

    const pillsData: any = [...filterPillsData];
    if (orgId) {
      const initialFilterObj = find(initialFilterList, { id: 'orgFilter' });

      const queryParamOrg = await getOrganization(orgId);
      const { status, data } = queryParamOrg;
      if (status == HTTP_STATUS.HTTP_OK) {
        const filteredOrg = [data];

        initialFilterObj.selectedFilter = filteredOrg;
        pillsData.push({
          name: filteredOrg[0].name,
          label: 'Organization',
          id: 'orgFilter'
        });
      }

      setFilterPillsData(pillsData);
      const currentFilterList = initialFilterList.map((obj: any) => {
        if (obj.id === 'orgFilter') {
          return { ...initialFilterObj };
        } else {
          return { ...obj };
        }
      });
      setFilterList(currentFilterList);
    } else {
      setFilterCallback();
    }
  };

  const loadOrgSearchData = async () => {
    const orgResponse = filterOrgNames(loadNextData.searchOrgName, orgList || []);
    updateFilterSetData(orgResponse);
    updateSelectedFilter(
      filterList,
      filterPillsData,
      '',
      fetchMoreOrgData,
      setFilterPillsData,
      setFilterList,
      () => {},
      () => {}
    );
  };

  const listFilters = [
    {
      id: 'status',
      label: 'T_STATUS',
      component: 'picklist',
      type: 'single',
      itemsList: AppConstants.FEATURES_STATUS_VALUES,
      selectedFilter: [],
      fieldKey: 'key',
      fieldValues: ['value'],
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: false,
      displayDelimiter: ' ',
      width: 'w-1/5'
    },
    {
      id: 'actions',
      label: 'T_FEATURES_ACTIONS',
      component: 'input',
      type: 'text',
      itemsList: [],
      selectedFilter: [],
      fieldKey: 'actions',
      disabled: false,
      searchable: true,
      searchPlaceholder: t('T_FEATURES_ACTIONS_PLACEHOLDER'),
      onlySearch: true,
      width: 'w-2/5'
    },
    {
      id: 'tags',
      label: 'T_FEATURES_TAGS',
      component: 'input',
      type: 'text',
      itemsList: [],
      selectedFilter: [],
      fieldKey: 'tags',
      disabled: false,
      searchable: true,
      searchPlaceholder: t('T_FEATURES_ACTIONS_PLACEHOLDER'),
      onlySearch: true,
      width: 'w-2/5'
    },
    {
      id: 'orgFilter',
      label: 'T_FILTER_KEY_ORGANIZATIONS',
      component: 'picklist',
      type: 'single',
      itemsList: [],
      selectedFilter: [],
      fieldKey: 'name',
      fieldValues: 'name',
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: true,
      width: 'w-3/5',
      fetchMoreData: fetchMoreOrgData,
      searchData: searchOrgData
    }
  ];

  const [filterList, setFilterList] = useState<Array<any>>(listFilters);
  const [filterPillsData, setFilterPillsData] = useState<Array<any>>([]);

  const globalFilters: any = useAppSelector((state) => state.templateTableReducer.tableOptions);

  let filtersKey: string = 'features-listing';

  useEffect(() => {
    const init = async () => {
      dispatch(
        setBreadcrump([
          {
            key: RouteConstants.ROUTE_FEATURES_LIST,
            label: `${t('T_FEATURES_LIST')}`
          }
        ])
      );
    };

    init();
  }, []);

  useEffect(() => {
    if (globalFilters?.[filtersKey]) {
      const { tableFilters, searchText, currentPage, size, sortName, sortType } =
        globalFilters[filtersKey];
      applyFilters(searchText, currentPage, size, sortName, sortType, tableFilters);
    } else {
      dispatch(
        setTableOptions({
          [filtersKey]: {
            searchText: '',
            currentPage: AppConstants.DEFAULT_PAGE,
            size: AppConstants.DEFAULT_PAGE_SIZE,
            sortName: AppConstants.DEFAULT_SORT_BY,
            sortType: AppConstants.DEFAULT_SORT_DESC,
            tableFilters: {}
          }
        })
      );
    }
  }, [globalFilters]);

  const getOrgForFeatures = async (loadNextData: any, filterList: any) => {
    const { searchOrgName } = loadNextData;
    const { paging } = find(filterList, { id: 'orgFilter' });
    const { size, page } = paging;
    let response;
    response = await getChildOrganizations(
      loggedInOrg?.organizationId,
      AppConstants.QUERY_PARAM_LEVEL_DESENDANTS,
      '',
      AppConstants.DEFAULT_PAGE,
      AppConstants.MAXIMUM_PAGE_SIZE,
      [`name,asc`]
    );

    const { status, data: organizationResp } = response;
    if (status == HTTP_STATUS.HTTP_OK) {
      const { data, paging } = organizationResp;
      return data;
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchOrgFilters();
    };
    init();
  }, [orgList]);

  const fetchOrgFilters = async () => {
    if (isEmpty(orgList)) {
      try {
        const [loggedInOrg, orgResponse] = await Promise.all([
          getUserLoggedInOrg(userProfile),
          getOrgForFeatures(loadNextData, filterList)
        ]);

        let data = orgResponse || [];
        if (!isEmpty(loggedInOrg)) {
          data.unshift(loggedInOrg);
        }

        if (data.length > 0) {
          dispatch(setOrgList(data));
        }
        updateFilterSetData(data);
      } catch (error) {
        updateFilterSetData([]);
      }
    } else {
      updateFilterSetData(orgList);
    }
  };

  const callAPI = async (
    name: string = '',
    tableFilters: any = formFilterData,
    page: number = AppConstants.DEFAULT_PAGE,
    size: number = AppConstants.DEFAULT_PAGE_SIZE,
    sort: string = AppConstants.DEFAULT_SORT_BY,
    sortType: string = AppConstants.DEFAULT_SORT_DESC
  ) => {
    try {
      const response = await listFeatures(name, page, size, sort, sortType, tableFilters);

      const { status, data } = response;
      if (status == HTTP_STATUS.HTTP_OK) {
        setPagingOpts(data.paging);
        setRows(data.data);
        setIsLoading(false);
        if (!data?.data?.length) {
          noDataAvailableMessage(setRows, setSize, setPagingOpts, response.data.paging, t);
        }
      } else {
        noDataAvailableMessage(setRows, setSize, setPagingOpts, response.data.paging, t);
        dispatch(
          setToastData({
            toastMessage: getFailureMessage(response),
            isToastActive: true,
            toastType: 'error'
          })
        );
        setIsLoading(false);
      }
    } catch (e) {
      dispatch(
        setToastData({
          toastMessage: t('T_ERROR_SOMETHING_WRONG'),
          isToastActive: true,
          toastType: 'error'
        })
      );
      setIsLoading(false);
    }
  };

  const featureSelector = (data: any) => {
    if (checkFeaturePermission(AppConstants.FEATURE_KEYS.FEATURE_MANAGEMENT_VIEW, false)) {
      dispatch(setFeatureData(data));
      navigate(RouteConstants.ROUTE_FEATURES_VIEW.replace(':id', data.id));
    } else {
      dispatch(
        setToastData({
          toastMessage: t('T_USER_NO_PERMISSION_TOAST_MESSAGE').replace(
            'Permission',
            t('T_FEATURE')
          ),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const refreshTableData = (
    name: string,
    page: number,
    size: number,
    sort: string = AppConstants.DEFAULT_SORT_BY,
    sortType: string = AppConstants.DEFAULT_SORT_DESC,
    filterData: any = ''
  ) => {
    setTableValues(name, page, size, sort, sortType, filterData);
  };

  const setTableValues = (
    name: string,
    page: number,
    size: number,
    sort: string = '',
    sortType: string = 'desc',
    filterData: any = ''
  ) => {
    setSize(size);
    setCurrentPage(page);
    setIsLoading(true);
    // const filter =
    //   filterData !== '' ? filterData : globalFilters[notificationListState].tableFilters;
    // callAPI(name, filter, page, size, sort, sortType);
  };

  //   const contextMenuHandler = (key: string, data: any) => {
  //     if (key === MenuConstants.TEMPLATE_MENU_COPY) {
  //       //copyTemplate(data);
  //       setTemplateCopyName(true);
  //       setTemplateCopyData(data);
  //     }
  //     if (key === MenuConstants.TEMPLATE_EDIT) {
  //       let { data: templateData } = cloneDeep(data);
  //       const { id: templateId } = templateData;
  //       navigate(`/templates/edit/${templateId}`);
  //     }
  //   };

  const featureHeaderBuilder = () => {
    let FEATURES_HEADERS: Array<ITableHeader> = [
      {
        key: 'status',
        description: `${t('T_STATUS')}`,
        sortEnabled: true,
        width: '10%',
        clickable: true,
        sortingInformation: {
          order: ''
        },
        styles: { borderRight: 'none' }
      },
      {
        key: 'name',
        description: `${t('T_NAME')}`,
        sortEnabled: true,
        width: '15%',
        nastedData: true,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          let name = data?.name;
          return <EllipsisDiv title={name}>{name}</EllipsisDiv>;
        }
      },
      {
        key: 'description',
        sortEnabled: true,
        description: `${t('T_DESCRIPTION')}`,
        width: '25%',
        clickable: true,
        sortingInformation: {
          order: ''
        }
      },
      {
        key: 'actions',
        sortEnabled: false,
        description: `${t('T_FEATURES_ACTIONS')}`,
        width: '15%',
        nastedData: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return data?.operations?.length
            ? data?.operations.map((operation: any) => operation.actionName).join(', ')
            : '';
        }
      },
      {
        key: 'tags',
        sortEnabled: false,
        description: `${t('T_FEATURES_TAGS')}`,
        width: '10%',
        nastedData: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return data?.tags?.length ? data?.tags.map((tag: any) => tag?.key).join(', ') : '';
        }
      },
      {
        key: 'createdOn',
        sortEnabled: true,
        description: `${t('T_CREATED_ON')}`,
        width: '12%',
        nastedData: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return moment(data?.createdOn).format(AppConstants.DEFAULT_DATE_FORMAT);
        }
      },
      {
        key: 'updatedOn',
        sortEnabled: true,
        description: `${t('T_LAST_MODIFIED_ON')}`,
        width: '13%',
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return moment(data?.updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT);
        }
      }
    ];
    return FEATURES_HEADERS;
  };

  const [tableHeaders, setTableHeaders] = useState<Array<ITableHeader>>(featureHeaderBuilder());

  const applyFilters = async (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setIsLoading(true);
    const appliedFilterList = Object.keys(filters);

    setFilterPillsData(updatePillsData(filters, appliedFilterList));
    setFilterList(updateFilterListSelected(filters));
    setParamObj(updateParamObj(filters, appliedFilterList));

    callAPI(search, filters, page, size, sortOrder, sortType);
    //getSurveyList({ page, size, search, sortOrder, sortType, filters });
  };

  const updateFilterListSelected = (appliedFilters: any) => {
    const initialFilterList: any = [...filterList];
    const updatedFilterList = initialFilterList.map((filterObj: any) => {
      const selectedFilter = appliedFilters[filterObj.id] ? appliedFilters[filterObj.id] : [];
      return { ...filterObj, selectedFilter };
    });

    return updatedFilterList;
  };

  const updatePillsData = (appliedFilters: any, appliedFilterList: any) => {
    const pillsData: any = [];
    if (appliedFilterList && appliedFilterList.length) {
      appliedFilterList.forEach((key: any) => {
        switch (key) {
          case 'status':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({ name: item.key, label: t('T_STATUS'), id: key });
              });
            }

            break;
          case 'actions':
            if (appliedFilters[key] && appliedFilters[key].length) {
              pillsData.push({
                name: appliedFilters[key],
                label: t('T_FEATURES_ACTIONS'),
                id: key,
                key: appliedFilters[key]
              });
            }
            break;
          case 'tags':
            if (appliedFilters[key] && appliedFilters[key].length) {
              pillsData.push({
                name: appliedFilters[key],
                label: t('T_FEATURES_TAGS'),
                id: key,
                key: appliedFilters[key]
              });
            }
            break;
          case 'orgFilter':
            if (appliedFilters[key] && appliedFilters[key].length) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({ name: item.name, label: 'Organization', id: key });
              });
            }
            break;
        }
      });
    }

    return pillsData;
  };

  const updateParamObj = (appliedFilters: any, appliedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    const initialFilterList: any = [...filterList];
    initialFilterList.forEach((filterObj: any) => {
      switch (filterObj.id) {
        case 'status':
          //initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'id').join(',');
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'key');
          break;
      }
    });

    return initialParamObj;
  };

  const removeFromPillsFilter = (pillsFilterObj: any) => {
    let initialFilterList: any = [...filterList];
    const initialFilterObj = find(initialFilterList, { id: pillsFilterObj.id });

    initialFilterObj.selectedFilter = [];

    const updatedFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === pillsFilterObj.id) {
        return { ...obj, ...initialFilterObj };
      } else {
        return { ...obj };
      }
    });

    updateParamObjAfterRemovePills(updatedFilterList);
    updatePillsDataAfterRemovePills(updatedFilterList);
    setFilterList(updatedFilterList);
  };

  const updatePillsDataAfterRemovePills = (updatedFilterList: any) => {
    const pillsData: any = [];
    updatedFilterList.forEach((obj: any) => {
      switch (obj.id) {
        case 'status':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({ name: item.value, label: t('T_STATUS'), id: obj.id });
          });
          break;
        case 'actions':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.value,
              label: t('T_FEATURES_ACTIONS'),
              id: item.id,
              key: item.key
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
        case 'tags':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.value,
              label: t('T_FEATURES_TAGS'),
              id: item.id,
              key: item.key
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
      }
    });
    setFilterPillsData(pillsData);
  };

  const updateParamObjAfterRemovePills = (updatedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    updatedFilterList.forEach((obj: any) => {
      initialParamObj[obj.id] = obj.selectedFilter;
      switch (obj.id) {
        case 'status':
          //initialParamObj[obj.id] = map(obj.selectedFilter, 'id').join(',');
          initialParamObj[obj.id] = map(obj.selectedFilter, 'id');
          break;
      }
    });
    setParamObj(initialParamObj);
    setIsLoading(true);
    const {
      searchText = '',
      currentPage: filterCurrentPage = AppConstants.DEFAULT_PAGE,
      size: filterSize = AppConstants.DEFAULT_PAGE_SIZE,
      sortName = '',
      sortType = ''
    } = globalFilters[filtersKey] || {};

    dispatch(
      setTableOptions({
        [filtersKey]: {
          searchText: searchText,
          currentPage: filterCurrentPage,
          size: filterSize,
          sortName: sortName,
          sortType: sortType,
          tableFilters: initialParamObj
        }
      })
    );
  };

  useDidMountEffect(() => {
    const {
      searchText = '',
      currentPage = AppConstants.DEFAULT_PAGE,
      size = AppConstants.DEFAULT_PAGE_SIZE,
      sortName = AppConstants.DEFAULT_SORT_BY, //category
      sortType = AppConstants.DEFAULT_SORT_DESC //asc
    } = globalFilters[filtersKey] || {};
    refreshTableData(
      searchText,
      AppConstants.DEFAULT_PAGE,
      size,
      sortName,
      sortType,
      formFilterData
    );
  }, [formFilterData]);

  return (
    <>
      <LoaderContainer isLoading={isLoading}>
        <CustomersTableContainer>
          {checkFeaturePermission(AppConstants.FEATURE_KEYS.FEATURE_MANAGEMENT_CREATE, false) && (
            <MenuButtonsPortal>
              <BiButton
                className={'flex flex-row bg-primary text-white'}
                type="button"
                onClick={() => {
                  navigate(RouteConstants.ROUTE_FEATURES_CREATE);
                }}
              >
                <img src={AddCircle} className="mr-2 w-6 text-center"></img>
                {t('T_FEATURE_CREATE_NEW_BUTTON')}
              </BiButton>
            </MenuButtonsPortal>
          )}
          <TemplateTable
            isLoading={isLoading}
            tableData={rows}
            size={size.toString()}
            setSize={setSize}
            currentPage={currentPage.toString()}
            cellSelector={featureSelector}
            templateSelector={featureSelector}
            onRefreshTableData={refreshTableData}
            tableHeaders={tableHeaders}
            setCurrentPage={setCurrentPage}
            setTableHeaders={setTableHeaders}
            containerClass="mt-0"
            searchPlaceholder={t('T_FEATURE_SEARCH_PLACEHOLDER')}
            pagingData={pagingOpts}
            showDeleteButton={true}
            theme={useTheme().theme}
            isSearchable
            filterWidth={80}
            totalDataLength={rows.length}
            isPaginated
            isFilterable
            filtersList={filterList}
            applyFilters={applyFilters}
            pillsData={filterPillsData}
            removeFilter={removeFromPillsFilter}
            defaultPillsLabel={t('T_FEATURES_FILTERS_DEFAULT_VIEW')}
            hasExternalCustomFilter={true}
            globalFilters={globalFilters}
            tablePage={filtersKey}
          />
        </CustomersTableContainer>
      </LoaderContainer>
    </>
  );
};

export default FeaturesList;
